import type { ComponentType,CSSProperties } from 'react';

/**
 * Minimal monochrome social-platform glyphs for the site footer's "Follow"
 * column. Same `<Svg>` wrapper contract as `LineIcons.tsx` (24×24 viewBox,
 * `currentColor`, optional `title`, `size` prop) so these read correctly
 * inline at ~20px alongside the rest of the brand icon set. Kept deliberately
 * simple (no platform wordmarks, no multi-tone gradients) — legible small,
 * not a pixel-accurate logo reproduction.
 */

type IconProps = {
  size?: number | undefined;
  title?: string | undefined;
  style?: CSSProperties | undefined;
};

function Svg({
  size = 24,
  title,
  style,
  children,
}: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
      style={{ display: 'block', flex: 'none', ...style }}
    >
      {title ? <title>{title}</title> : null}
      {children}
    </svg>
  );
}

/** Instagram — rounded square + ring + shutter dot. */
export function IconInstagram(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="3.5" y="3.5" width="17" height="17" rx="5" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="12" cy="12" r="4.2" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="16.6" cy="7.4" r="1.1" fill="currentColor" />
    </Svg>
  );
}

/** Substack — the stacked-lines "S" mark. */
export function IconSubstack(p: IconProps) {
  return (
    <Svg {...p}>
      <path d="M5 5h14v2.4H5z" fill="currentColor" />
      <path d="M5 9.4h14v2.4H5z" fill="currentColor" />
      <path d="M5 13.8h14V19L12 15.4 5 19z" fill="currentColor" />
    </Svg>
  );
}

/** Facebook — the "f" flag mark. */
export function IconFacebook(p: IconProps) {
  return (
    <Svg {...p}>
      <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M13.6 20V12.6h2l.3-2.4h-2.3V8.7c0-.7.2-1.2 1.2-1.2h1.3V5.4A17 17 0 0 0 14.4 5c-1.9 0-3.2 1.1-3.2 3.3v1.9H9v2.4h2.2V20z"
        fill="currentColor"
      />
    </Svg>
  );
}

/** TikTok — the musical-note-in-motion mark. */
export function IconTikTok(p: IconProps) {
  return (
    <Svg {...p}>
      <path
        d="M13 4v10.2a2.9 2.9 0 1 1-2.4-2.85"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <path
        d="M13 4a5 5 0 0 0 5 4.6"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </Svg>
  );
}

/** Threads — the interlocking "@"-like loop. */
export function IconThreads(p: IconProps) {
  return (
    <Svg {...p}>
      <path
        d="M12 3.5c-4.4 0-7 2.7-7 8.5s2.6 8.5 7 8.5c3.6 0 5.7-1.7 5.7-4.4 0-2.3-1.6-3.6-4.2-3.7-2.4-.1-3.8.8-3.8 2.2 0 1.1.9 1.8 2.2 1.8 1.7 0 2.8-1.1 2.9-3 .1-2.6-1.2-4.4-3.6-4.4-1.6 0-2.8.7-3.5 1.8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

/** Bluesky — the butterfly wings mark. */
export function IconBluesky(p: IconProps) {
  return (
    <Svg {...p}>
      <path
        d="M12 8C10 5 5 4.5 4.5 7C4 9.5 4.5 13 6 14C8 15 11 14 12 15C13 14 16 15 18 14C19.5 13 20 9.5 19.5 7C19 4.5 14 5 12 8Z"
        fill="currentColor"
      />
    </Svg>
  );
}

/** YouTube — rounded play-button mark. */
export function IconYouTube(p: IconProps) {
  return (
    <Svg {...p}>
      <rect x="2.5" y="6" width="19" height="12" rx="4" stroke="currentColor" strokeWidth="1.6" />
      <path d="M10.2 9.5v5l4.4-2.5z" fill="currentColor" />
    </Svg>
  );
}

export interface SocialLink {
  readonly name: string;
  readonly href: string;
  readonly Icon: ComponentType<IconProps>;
}

/** Hardcoded social-platform hrefs for the footer "Follow" column. */
export const SOCIAL_LINKS: readonly SocialLink[] = [
  { name: 'Instagram', href: 'https://instagram.com/wwaavehello', Icon: IconInstagram },
  { name: 'Substack', href: 'https://wwaave.substack.com', Icon: IconSubstack },
  { name: 'Facebook', href: 'https://facebook.com/wwaavehello', Icon: IconFacebook },
  { name: 'TikTok', href: 'https://tiktok.com/@wwaavehello', Icon: IconTikTok },
  { name: 'Threads', href: 'https://threads.net/@wwaavehello', Icon: IconThreads },
  { name: 'Bluesky', href: 'https://bsky.app/profile/wwaavehello.bsky.social', Icon: IconBluesky },
  { name: 'YouTube', href: 'https://youtube.com/@wwaavehello', Icon: IconYouTube },
];
